import csvToJson from 'csvtojson';

/**
 * CSV parsing + validation for expense allocation imports (Expense Group
 * settings and one-off Expense Allocation). Shared so both modals reject the
 * same malformed files with the same messages instead of silently dropping
 * columns — see ENG-499, where a duplicated "Class" header hid the
 * percentages and the user only saw "Total percentage must equal 100."
 */

interface Option {
	title: string;
	value: string;
}

export interface AllocationCsvRow {
	customer?: string;
	class?: string;
	location?: string;
	percentage?: number;
}

export const ALLOCATION_CSV_COLUMNS = ['Customer', 'Class', 'Location', 'Percentage'];

const EXPECTED_COLUMNS_HINT = `Expected columns: ${ALLOCATION_CSV_COLUMNS.join(', ')}.`;
const PERCENTAGE_HEADERS = ['percentage', 'percent'];
const MAX_REPORTED_ERRORS = 10;

// Allowed gap between the total and 100, in hundredths (±0.01). Mirrors the backend.
const PERCENTAGE_TOTAL_TOLERANCE_HUNDREDTHS = 1;

/** True when the total is 100 to within ±0.01, ignoring binary float noise. */
export const isPercentageTotalValid = (total: number) =>
	Math.abs(Math.round(Math.round(total * 100 * 1e6) / 1e6) - 10000) <=
	PERCENTAGE_TOTAL_TOLERANCE_HUNDREDTHS;

/**
 * Parses a percentage cell, allowing a trailing "%" ("27%", "27.5 %").
 * Returns undefined for an empty cell and NaN for a non-numeric one.
 */
export const parsePercentageCell = (value: unknown): number | undefined => {
	if (value === undefined || value === null) return undefined;
	const cleaned = String(value).trim().replace(/\s*%$/, '');
	if (cleaned === '') return String(value).trim() === '' ? undefined : NaN;
	return Number(cleaned);
};

const findValueByTitle = (title: string, list: Option[]) => {
	const wanted = title.toLowerCase().trim();
	return list.find((option) => option.title.toLowerCase().trim() === wanted)
		?.value;
};

export const parseAllocationCsv = async (
	text: string,
	lookups: { customers: Option[]; classes: Option[]; locations: Option[] }
): Promise<{ rows: AllocationCsvRow[]; errors: string[] }> => {
	let rawHeaders: string[] = [];
	const json: Record<string, string>[] = await csvToJson({ trim: true })
		.on('header', (header: string[]) => (rawHeaders = header))
		.fromString(text.charCodeAt(0) === 0xfeff ? text.slice(1) : text); // Excel "CSV UTF-8" adds a BOM

	// csvtojson keeps only the last of two same-named columns, so duplicates
	// must be caught from the raw header row
	const headers = rawHeaders.map((h) => h.trim());
	const seen = new Set<string>();
	const duplicates = new Set<string>();
	headers.forEach((h) => {
		const key = h.toLowerCase();
		if (seen.has(key)) duplicates.add(h);
		seen.add(key);
	});
	if (duplicates.size > 0) {
		return {
			rows: [],
			errors: Array.from(duplicates).map(
				(h) =>
					`Column "${h}" appears more than once in the header row. ${EXPECTED_COLUMNS_HINT}`
			),
		};
	}

	const headerFor = (names: string[]) =>
		headers.find((h) => names.includes(h.toLowerCase()));
	const percentageHeader = headerFor(PERCENTAGE_HEADERS);
	if (!percentageHeader) {
		return {
			rows: [],
			errors: [`The file has no "Percentage" column. ${EXPECTED_COLUMNS_HINT}`],
		};
	}
	const customerHeader = headerFor(['customer']);
	const classHeader = headerFor(['class']);
	const locationHeader = headerFor(['location']);

	const errors: string[] = [];
	const rows: AllocationCsvRow[] = [];

	json.forEach((item, index) => {
		const line = index + 2; // header is line 1
		const titles = {
			customer: (customerHeader && item[customerHeader]) || '',
			class: (classHeader && item[classHeader]) || '',
			location: (locationHeader && item[locationHeader]) || '',
		};
		const rawPercentage = item[percentageHeader];
		const percentage = parsePercentageCell(rawPercentage);

		if (percentage === undefined && !titles.customer && !titles.class && !titles.location) {
			return; // blank line
		}
		if (percentage === undefined) {
			errors.push(`Row ${line}: percentage is missing.`);
		} else if (Number.isNaN(percentage)) {
			errors.push(`Row ${line}: percentage "${rawPercentage}" is not a number.`);
		}

		const resolve = (kind: 'customer' | 'class' | 'location', list: Option[]) => {
			const title = titles[kind];
			if (!title) return undefined;
			const value = findValueByTitle(title, list);
			// 0% rows are discarded on save, so an unknown name there doesn't block the import
			if (!value && percentage) {
				errors.push(`Row ${line}: ${kind} "${title}" was not found in QuickBooks.`);
			}
			return value;
		};

		rows.push({
			customer: resolve('customer', lookups.customers),
			class: resolve('class', lookups.classes),
			location: resolve('location', lookups.locations),
			percentage,
		});
	});

	if (errors.length > MAX_REPORTED_ERRORS) {
		const hidden = errors.length - MAX_REPORTED_ERRORS;
		errors.splice(MAX_REPORTED_ERRORS, hidden, `…and ${hidden} more.`);
	}
	return { rows, errors };
};
